import * as React from 'react';
import { LoaderCircle } from 'lucide-react';
import { Toaster as Sonner, toast, type ExternalToast, type ToasterProps } from 'sonner';

import { toneIcon } from '../../lib/tone';

/**
 * Toasts — the handoff's section 03.
 *
 * shadcn's `sonner` component, with its `next-themes` lookup removed: the apps
 * are light-only and are not Next, so asking a theme provider that does not
 * exist would just return "system" and let the OS repaint the toast.
 *
 * Sonner already implements the handoff's mechanics — bottom-right, newest on
 * top, pause on hover and focus — so the only things set here are the numbers
 * the handoff names and the colours, which come from the toast tokens in
 * Tier 3 rather than from sonner's defaults.
 *
 * The mark is a lucide icon rather than the handoff's flat dot. A dot carries
 * its meaning in colour alone; the icon says which kind of message this is
 * without relying on it, which is the handoff's own accessibility rule two
 * sections further down. Which icon means what comes from `lib/tone`, so a
 * toast and the alert it is the transient cousin of carry the same mark.
 *
 * Sonner's slot names are its API, not ours: its `error` is the tone this
 * system calls `danger`, and its `info` is the untinted `neutral`. Translated
 * here, once.
 */
const { danger: DangerMark, warning: WarningMark, success: SuccessMark, neutral: NeutralMark } =
  toneIcon;

/** Success clears itself after four seconds. */
const SUCCESS_DURATION_MS = 4000;

/** Anything the user must read stays until they dismiss it. */
const PERSIST = Number.POSITIVE_INFINITY;

/** Three at a time; a fourth pushes the oldest out. */
const VISIBLE_TOASTS = 3;

function Toaster(props: ToasterProps) {
  return (
    <Sonner
      theme="light"
      position="bottom-right"
      visibleToasts={VISIBLE_TOASTS}
      duration={SUCCESS_DURATION_MS}
      className="toaster group"
      icons={{
        success: <SuccessMark className="size-4 text-(--toast-mark-success)" />,
        info: <NeutralMark className="size-4 text-(--toast-mark-info)" />,
        warning: <WarningMark className="size-4 text-(--toast-mark-warning)" />,
        error: <DangerMark className="size-4 text-(--toast-mark-danger)" />,
        loading: <LoaderCircle className="size-4 animate-spin text-(--toast-body-fg)" />,
      }}
      style={
        {
          '--normal-bg': 'var(--toast-bg)',
          '--normal-text': 'var(--toast-fg)',
          '--normal-border': 'var(--toast-edge)',
        } as React.CSSProperties
      }
      toastOptions={{
        classNames: {
          toast: 'items-start gap-3 rounded-card shadow-lift',
          title: 'text-sm font-semibold text-(--toast-fg)',

          /* The `!` is load-bearing, and this is the one place in the shared
           * components where that is the right answer rather than a patch over
           * a specificity mistake.
           *
           * Sonner's own stylesheet hardcodes the description to `#3f3f3f` —
           * a dark grey, chosen for its default light toast. On this dark
           * panel that is 1.9:1 and effectively invisible. The rule is both
           * unlayered and three selectors deep, and unlayered CSS outranks
           * anything in a cascade layer, so a Tailwind utility cannot reach it
           * without the important flag.
           *
           * `tracking-normal` undoes the type step's own letter-spacing: the
           * xs step is built for eyebrows and meta, which are set wide on
           * purpose, and at that tracking a full sentence stops reading as
           * one. */
          description: 'text-xs leading-normal tracking-normal text-(--toast-body-fg)!',

          /* Mono, uppercase and mint at the far end of the strip — the handoff
           * draws the action as a label rather than a button, so it reads as
           * the one thing to press without competing with the message. */
          actionButton:
            'ml-auto bg-transparent font-mono text-2xs font-semibold uppercase text-(--toast-action-fg)',
          cancelButton:
            'ml-auto bg-transparent font-mono text-2xs uppercase text-(--toast-body-fg)',
          closeButton: 'text-(--toast-fg)',
        },
      }}
      {...props}
    />
  );
}

/**
 * The handoff's timing rule, applied once here instead of at every call site:
 * success goes away on its own, everything else waits to be dismissed.
 *
 * `toast` itself is re-exported for the cases sonner covers and this does not
 * — promises, loading states, custom content.
 */
const notify = {
  success: (message: React.ReactNode, options?: ExternalToast) =>
    toast.success(message, { duration: SUCCESS_DURATION_MS, ...options }),
  warning: (message: React.ReactNode, options?: ExternalToast) =>
    toast.warning(message, { duration: PERSIST, ...options }),
  error: (message: React.ReactNode, options?: ExternalToast) =>
    toast.error(message, { duration: PERSIST, ...options }),
  info: (message: React.ReactNode, options?: ExternalToast) =>
    toast.info(message, { duration: PERSIST, ...options }),
  dismiss: toast.dismiss,
};

export { Toaster, notify, toast };
